import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getVeiculos, getHistorico } from '../services/api';

function pad(n) { return String(n).padStart(2, '0'); }

function dataParaInput(d) {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function formatarData(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('pt-BR');
}

function statusIgnicao(v) {
    if (v === true) return 'Ligada';
    if (v === false) return 'Desligada';
    return '—';
}

const inputStyle = {
    width: '100%', border: 'none', outline: 'none', fontSize: 15,
    fontFamily: 'var(--ios-font)', color: 'var(--ios-label)',
    background: 'transparent', padding: '8px 0 10px', boxSizing: 'border-box',
};

function FilterField({ label, children }) {
    return (
        <div style={{ borderBottom: '1px solid var(--ios-separator)' }}>
            <div style={{ padding: '0 16px' }}>
                <span style={{ fontSize: 11, color: 'var(--ios-gray)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', paddingTop: 9 }}>
                    {label}
                </span>
                {children}
            </div>
        </div>
    );
}

export default function Relatorio({ onFechar, configuracao = {} }) {
    const [veiculos, setVeiculos] = useState([]);
    const [veiculoId, setVeiculoId] = useState('');
    const [veiculoNome, setVeiculoNome] = useState('');
    const [inicio, setInicio] = useState(dataParaInput(Date.now() - 86400000));
    const [fim, setFim] = useState(dataParaInput(Date.now()));
    const [posicoes, setPosicoes] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        getVeiculos().then(r => setVeiculos(r.data));
    }, []);

    const buscar = async () => {
        if (!veiculoId) return setErro('Selecione um veículo');
        setErro(''); setCarregando(true); setPosicoes([]);
        try {
            const res = await getHistorico(veiculoId, new Date(inicio).toISOString(), new Date(fim).toISOString());
            if (res.data.length === 0) setErro('Nenhum registro encontrado no período');
            setPosicoes(res.data);
            const v = veiculos.find(v => String(v.id) === String(veiculoId));
            setVeiculoNome(v ? `${v.nome} — ${v.placa}` : '');
        } catch(e) {
            setErro('Erro ao buscar dados');
        } finally {
            setCarregando(false);
        }
    };

    const exportarExcel = () => {
        if (posicoes.length === 0) return;
        const nomePlataforma = configuracao.nome_plataforma || 'Sigatron';
        const cabecalho = [
            [nomePlataforma],
            [`Relatório de Rastreamento — ${veiculoNome}`],
            [`Período: ${new Date(inicio).toLocaleString('pt-BR')} até ${new Date(fim).toLocaleString('pt-BR')}`],
            [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
            [],
        ];
        const colunas = ['#', 'Data/Hora GPS', 'Data/Hora GPRS', 'Latitude', 'Longitude', 'Velocidade (km/h)', 'Ignição', 'Bateria (V)', 'Satélites', 'GPS Válido', 'Rastreador', 'IMEI'];
        const linhas = posicoes.map((p, i) => [
            i + 1,
            formatarData(p.data_rastreador),
            formatarData(p.recebido_em),
            p.latitude,
            p.longitude,
            p.velocidade || 0,
            statusIgnicao(p.ignicao),
            p.bateria != null ? Number(p.bateria).toFixed(1) : '—',
            p.satellites || '—',
            p.gps_valido ? 'Sim' : 'Não',
            p.rastreador || 1,
            p.imei_rastreador || '—',
        ]);
        const ws = XLSX.utils.aoa_to_sheet([...cabecalho, colunas, ...linhas]);
        ws['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 18 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Relatório');
        XLSX.writeFile(wb, `${nomePlataforma}_${veiculoNome.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const velMax = posicoes.length > 0 ? Math.max(...posicoes.map(p => p.velocidade || 0)) : 0;
    const velMedia = posicoes.length > 0 ? Math.round(posicoes.reduce((s, p) => s + (p.velocidade || 0), 0) / posicoes.length) : 0;
    const emMovimento = posicoes.filter(p => (p.velocidade || 0) > 2).length;
    const ligada = posicoes.filter(p => p.ignicao === true).length;

    const distancia = () => {
        let total = 0;
        for (let i = 1; i < posicoes.length; i++) {
            const R = 6371;
            const dLat = (posicoes[i].latitude - posicoes[i-1].latitude) * Math.PI / 180;
            const dLng = (posicoes[i].longitude - posicoes[i-1].longitude) * Math.PI / 180;
            const a = Math.sin(dLat/2)**2 + Math.cos(posicoes[i-1].latitude * Math.PI/180) *
                Math.cos(posicoes[i].latitude * Math.PI/180) * Math.sin(dLng/2)**2;
            total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        }
        return total.toFixed(2);
    };

    const stats = posicoes.length > 0 ? [
        { label: 'Registros',      valor: posicoes.length,     cor: '#007aff' },
        { label: 'Distância',      valor: distancia() + ' km', cor: '#5856d6' },
        { label: 'Vel. máxima',    valor: velMax + ' km/h',    cor: '#ff9500' },
        { label: 'Vel. média',     valor: velMedia + ' km/h',  cor: '#34c759' },
        { label: 'Em movimento',   valor: emMovimento,         cor: '#34c759' },
        { label: 'Ign. ligada',    valor: ligada,              cor: '#ff3b30' },
    ] : [];

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            display: 'flex', flexDirection: 'column',
            background: 'var(--ios-gray6)',
            fontFamily: 'var(--ios-font)',
        }}>
            {/* ── Navbar iOS ── */}
            <div style={{
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 20px', height: 52, flexShrink: 0,
            }}>
                <span style={{ fontWeight: 700, fontSize: 17, color: '#1c1c1e', letterSpacing: -0.3 }}>
                    Relatório Técnico
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    {posicoes.length > 0 && (
                        <button onClick={exportarExcel} className="ios-btn ios-btn-green ios-btn-sm">
                            Exportar Excel
                        </button>
                    )}
                    <button onClick={onFechar} className="ios-btn ios-btn-gray ios-btn-sm">
                        Fechar
                    </button>
                </div>
            </div>

            {/* ── Filtros ── */}
            <div style={{ padding: '16px 20px', flexShrink: 0, borderBottom: '1px solid var(--ios-separator)' }}>
                <p style={{ fontSize: 12, color: 'var(--ios-gray)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 4px 6px', fontWeight: 500 }}>Filtros</p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {/* Campos agrupados */}
                    <div className="ios-group" style={{ display: 'flex', flex: 1, minWidth: 280 }}>
                        <FilterField label="Veículo">
                            <select style={{ ...inputStyle, appearance: 'none' }} value={veiculoId} onChange={e => setVeiculoId(e.target.value)}>
                                <option value="">Selecione um veículo...</option>
                                {veiculos.map(v => <option key={v.id} value={v.id}>{v.nome} — {v.placa}</option>)}
                            </select>
                        </FilterField>
                    </div>

                    <div className="ios-group" style={{ display: 'flex', flex: 1, minWidth: 280 }}>
                        <FilterField label="Data início">
                            <input type="datetime-local" style={inputStyle} value={inicio} onChange={e => setInicio(e.target.value)} />
                        </FilterField>
                        <FilterField label="Data fim">
                            <input type="datetime-local" style={inputStyle} value={fim} onChange={e => setFim(e.target.value)} />
                        </FilterField>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={buscar} disabled={carregando} className="ios-btn ios-btn-primary">
                            {carregando ? 'Buscando...' : 'Buscar'}
                        </button>
                        {erro && <span style={{ fontSize: 13, color: '#ff3b30' }}>{erro}</span>}
                    </div>
                </div>
            </div>

            {/* ── Cards de estatísticas ── */}
            {stats.length > 0 && (
                <div style={{ display: 'flex', gap: 10, padding: '14px 20px', flexShrink: 0, flexWrap: 'wrap', borderBottom: '1px solid var(--ios-separator)' }}>
                    {stats.map(s => (
                        <div key={s.label} style={{
                            background: 'white', borderRadius: 12, padding: '10px 16px',
                            boxShadow: 'var(--ios-shadow)', minWidth: 90, flex: 1,
                        }}>
                            <div style={{ fontSize: 18, fontWeight: 700, color: s.cor, letterSpacing: -0.5 }}>{s.valor}</div>
                            <div style={{ fontSize: 11, color: 'var(--ios-gray)', marginTop: 2, fontWeight: 500 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Tabela ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                {posicoes.length > 0 && (
                    <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--ios-shadow)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: 'var(--ios-gray6)', borderBottom: '1px solid var(--ios-separator)' }}>
                                    {['#', 'Data/Hora GPS', 'Data/Hora GPRS', 'Latitude', 'Longitude', 'Vel.', 'Ignição', 'Bateria', 'Sats', 'GPS', 'Rastr.', 'IMEI'].map((h, i) => (
                                        <th key={h} style={{
                                            padding: '10px 10px', textAlign: i <= 2 ? 'left' : 'center',
                                            fontWeight: 600, fontSize: 11, color: 'var(--ios-gray)',
                                            textTransform: 'uppercase', letterSpacing: 0.4,
                                            whiteSpace: 'nowrap', position: 'sticky', top: 0,
                                            background: 'var(--ios-gray6)',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {posicoes.map((p, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid var(--ios-separator)', background: 'white' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--ios-gray6)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <td style={{ padding: '8px 10px', color: 'var(--ios-gray)', fontWeight: 500 }}>{i + 1}</td>
                                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--ios-label2)' }}>{formatarData(p.data_rastreador)}</td>
                                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--ios-label2)' }}>{formatarData(p.recebido_em)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'monospace', fontSize: 11, color: 'var(--ios-label)' }}>{p.latitude}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'monospace', fontSize: 11, color: 'var(--ios-label)' }}>{p.longitude}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            <span style={{
                                                background: (p.velocidade||0) > 0 ? 'rgba(52,199,89,0.12)' : 'var(--ios-gray6)',
                                                color: (p.velocidade||0) > 0 ? '#34c759' : 'var(--ios-gray)',
                                                borderRadius: 6, padding: '2px 8px', fontWeight: 600,
                                            }}>{p.velocidade || 0}</span>
                                        </td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            <span style={{
                                                background: p.ignicao === true ? 'rgba(255,59,48,0.1)' : 'var(--ios-gray6)',
                                                color: p.ignicao === true ? '#ff3b30' : 'var(--ios-gray)',
                                                borderRadius: 6, padding: '2px 8px', fontWeight: 500, fontSize: 11,
                                            }}>{statusIgnicao(p.ignicao)}</span>
                                        </td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--ios-label2)' }}>
                                            {p.bateria != null ? Number(p.bateria).toFixed(1) : '—'}
                                        </td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--ios-label2)' }}>{p.satellites || '—'}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            <span style={{
                                                background: p.gps_valido ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.1)',
                                                color: p.gps_valido ? '#34c759' : '#ff3b30',
                                                borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 11,
                                            }}>{p.gps_valido ? 'OK' : 'Falha'}</span>
                                        </td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                            <span style={{
                                                background: p.rastreador === 2 ? 'rgba(88,86,214,0.12)' : 'rgba(0,122,255,0.1)',
                                                color: p.rastreador === 2 ? '#5856d6' : '#007aff',
                                                borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 11,
                                            }}>R{p.rastreador || 1}</span>
                                        </td>
                                        <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'monospace', fontSize: 11, color: 'var(--ios-label2)' }}>
                                            {p.imei_rastreador || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {posicoes.length === 0 && !carregando && (
                    <div style={{ textAlign: 'center', padding: 60, color: 'var(--ios-gray)' }}>
                        <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📋</div>
                        <div style={{ fontSize: 16, fontWeight: 500 }}>Nenhum dado</div>
                        <div style={{ fontSize: 13, marginTop: 4 }}>Selecione um veículo e período para buscar</div>
                    </div>
                )}
            </div>
        </div>
    );
}
